// @i18n-ignore-file
import { useStatsigClient } from '@statsig/react-bindings'

import { MODULES, FEATURES } from '@src/quince-site-common/src/constants/template-info'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

// Statsig dynamic config that maps PL widget "slots" → their DB-unique widget id. Sourced from Statsig
// (not hardcoded) so the backend can update an id without an SSR deploy. Console:
//   dynamic_configs/pl_widget_ids
const PL_WIDGET_IDS_CONFIG_NAME = 'pl_widget_ids'

/**
 * Read a single PL widget id from the `pl_widget_ids` Statsig dynamic config.
 *
 * Returns the DB-unique widget id for the given key, or `undefined` when the key is absent / the config
 * is unavailable — callers treat `undefined` as "feature inert" (no forced load / scroll), so a missing
 * config never breaks the page. Safe to call at the page root: errors are captured, not thrown.
 */
export const useStatsigPlWidgetId = (key: string): string | undefined => {
  const { getDynamicConfig } = useStatsigClient()
  try {
    const id = getDynamicConfig(PL_WIDGET_IDS_CONFIG_NAME).get(key, '')
    return id || undefined
  } catch (error) {
    captureException(error as Error, {
      module: MODULES.PL,
      feature: FEATURES.LAYOUT_PAGINATION,
      additionalTags: { source: 'useStatsigPlWidgetId' },
      extraInfo: { configKey: PL_WIDGET_IDS_CONFIG_NAME, key },
    })
    return undefined
  }
}

/**
 * Whether a widget matches a `pl_widget_ids` config value that may list MORE THAN ONE id.
 *
 * A logical PDP slot ("More to Explore") can be a DIFFERENT widget across breakpoints — desktop renders
 * it as HORIZONTAL_PRODUCT_LIST, mobile as PRODUCT_GRID_CAROUSEL — and those carry different ids. So the
 * config value is treated as a comma-separated list of ids; the widget matches when its DB id (`id`) or
 * API-request key (`widgetId`) equals ANY entry. A single-id value (e.g. `reviewsFull`) still works —
 * it's just a one-element list. Empty/undefined config → no match (feature inert).
 */
export const matchesPlWidgetId = (configValue: string | undefined, id?: string, widgetId?: string): boolean => {
  if (!configValue) return false
  return configValue
    .split(',')
    .map(entry => entry.trim())
    .filter(Boolean)
    .some(entry => entry === id || entry === widgetId)
}
